import { useId, useState } from 'react';
import { t } from '../../i18n/i18n';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * An `InfoRow` that opens onto several destinations — a platform with a
 * Windows installer, a macOS installer and a web app is one row to scan, not
 * three. Drawn as the same raised row so the two sit together in one list.
 *
 * `options` is `[{ id, label, note?, logo?, href }]`; each becomes an outbound link
 * under the header. An option without an `href` renders as plain text, for the
 * same reason `InfoRow` does.
 *
 * The panel stays mounted and is collapsed with a grid-row transition, so it
 * animates without a measured height and `inert` keeps the hidden links out of
 * the tab order.
 */
export default function InfoAccordion({ logo, label, note, options = [], defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className={`ip-acc ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="ip-row ip-acc__head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((was) => !was)}
      >
        {logo && (
          <span className="ip-row__logo">
            <img src={logo} alt="" />
          </span>
        )}
        <span className="ip-row__copy">
          <span className="ip-row__name">{t(label)}</span>
          {note && <span className="ip-row__note">{t(note)}</span>}
        </span>
        <span className="ip-acc__count">{t('{{count}} options', { count: options.length })}</span>
        <span className="icon ip-acc__chevron" style={icon('chevron-down')} aria-hidden="true" />
      </button>

      <div id={panelId} className="ip-acc__panel" inert={!open ? '' : undefined}>
        <div className="ip-acc__inner">
          <ul className="ip-acc__list">
            {options.map((opt) => {
              const body = (
                <>
                  {opt.logo && (
                    <span className="ip-row__logo">
                      <img src={opt.logo} alt="" />
                    </span>
                  )}
                  <span className="ip-row__copy">
                    <span className="ip-row__name">{t(opt.label)}</span>
                    {opt.note && <span className="ip-row__note">{t(opt.note)}</span>}
                  </span>
                  {opt.href && <span className="ip-row__go">{t('Get')}</span>}
                </>
              );
              return (
                <li key={opt.id}>
                  {opt.href ? (
                    <a
                      className="ip-row ip-acc__option"
                      href={opt.href}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {body}
                    </a>
                  ) : (
                    <div className="ip-row ip-acc__option">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
